import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFlowOwner } from "@/components/FlowAccessGate";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, ArrowLeft, Check, Share2, Gift, Users, Copy, Twitter, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import type { Flow, PricingTier } from "@/types";
import { getGeoData } from "@/utils/geolocation";
import { getDefaultThankYouMessage, getDefaultThankYouNote } from "./CreateFlow/utils/thankYouDefaults";
import { resolveThankYouStyles } from "./CreateFlow/lib/pricingTypography";
import { getFlowAccent, contrastTextFor } from "@/utils/flowAccent";

export default function ThankYou() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useFlowOwner();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const animationFrameId = useRef<number>();
  const redirectTimeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) return;
        
        const { data, error } = await supabase
          .from('flows')
          .select('*')
          .eq('id', flowId)
          .single();
        
        if (error) {
          throw error;
        }

        setFlow(data as Flow);
        
        // Record conversion for all flow types
        if (data) {
          await recordConversion(flowId, data.flow_type);
        }
        
        // Get selected tier from session storage (scoped by flowId so it
        // doesn't leak across flows).
        const storedTier = sessionStorage.getItem(`selectedPricingTier:${flowId}`);
        if (storedTier) {
          setSelectedTier(JSON.parse(storedTier));
        }
      } catch (error) {
        // Error loading flow
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlow();
  }, [flowId]);

  useEffect(() => {
    if (flow?.redirect_url) {
      const delay = flow.redirect_delay || 5;
      setTimeLeft(delay);
      startCountdown();
    }
  }, [flow]);

  const recordConversion = async (flow_id: string, flowType: string) => {
    try {
      // Skip recording conversions for flows that already record in their respective components
      // - signup/waitlist: recorded in SignupFlow component
      // - payment/subscription/preorder/discount/pricing_table: recorded in PaymentFlow component
      const skipFlowTypes = ["signup", "waitlist", "payment", "subscription", "preorder", "discount", "pricing_table"];
      if (skipFlowTypes.includes(flowType)) {
        return;
      }

      // Check if flow is embedded - if it is, skip recording since SessionTracker handles it
      const isEmbedded = window.self !== window.top;
      if (isEmbedded) {
        return;
      }
      
      // Generate a unique key for this flow conversion
      const conversionKey = `flow_conversion_${flow_id}`;
      
      // Check if this user has already converted this flow today
      const viewDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
      const storedConversion = localStorage.getItem(conversionKey);
      
      // If user already converted this flow today, don't record again
      if (storedConversion === viewDate) {
        return;
      }
      
      // Store the conversion in localStorage to prevent duplicates
      localStorage.setItem(conversionKey, viewDate);

      // Get geolocation data
      const geoData = await getGeoData();

      // Generate a unique identifier for the user if not exists
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }

      // Get email from session storage
      const email = sessionStorage.getItem('flow_email') || sessionStorage.getItem('referral_email');

      const { error } = await supabase.from('flow_analytics').insert([
        {
          flow_id,
          event_type: 'conversion',
          email: email || undefined,
          referrer: document.referrer || 'direct',
          is_embedded: isEmbedded,
          user_agent: navigator.userAgent,
          path: window.location.pathname,
          country: geoData?.country || 'Unknown',
          user_identifier: userIdentifier,
          view_date: viewDate
        }
      ]);

      if (error) {
        throw error;
      }
      
      // Clear the stored data
      sessionStorage.removeItem('flow_email');
      sessionStorage.removeItem('referral_email');
      sessionStorage.removeItem(`selectedPricingTier:${flowId}`);
      // Also clean up the legacy unscoped key in case anything stale survived.
      sessionStorage.removeItem('selectedPricingTier');
    } catch (error) {
      // Don't show error to user as this is not critical
    }
  };

  const startCountdown = () => {
    if (!flow?.redirect_url) return;

    const redirectDelay = flow.redirect_delay || 5;
    const startTime = Date.now();
    const endTime = startTime + (redirectDelay * 1000);

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const newProgress = ((redirectDelay * 1000) - remaining) / (redirectDelay * 1000) * 100;
      
      setProgress(Math.min(newProgress, 100));
      setTimeLeft(Math.ceil(remaining / 1000));

      if (remaining > 0) {
        animationFrameId.current = requestAnimationFrame(updateProgress);
      } else {
        handleRedirect();
      }
    };

    animationFrameId.current = requestAnimationFrame(updateProgress);
    
    // Backup timeout in case the animation frame doesn't trigger the redirect
    redirectTimeoutId.current = setTimeout(() => {
      handleRedirect();
    }, redirectDelay * 1000);
  };

  const handleRedirect = () => {
    if (!flow?.redirect_url) return;

    let redirectUrl = flow.redirect_url;
    if (!redirectUrl.startsWith('http://') && !redirectUrl.startsWith('https://')) {
      redirectUrl = `https://${redirectUrl}`;
    }
    window.location.href = redirectUrl;
  };

  // Cancels the auto-redirect timer + countdown so the user isn't yanked
  // away while interacting with the page (e.g. opening a share link).
  const cancelRedirect = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = undefined;
    }
    if (redirectTimeoutId.current) {
      clearTimeout(redirectTimeoutId.current);
      redirectTimeoutId.current = undefined;
    }
    setProgress(0);
    setTimeLeft(0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-5/12">
              <div className="h-8 w-3/4 mb-2 bg-slate-200 animate-pulse rounded" />
              <div className="h-6 w-full mb-4 bg-slate-200 animate-pulse rounded" />
              <div className="h-40 w-full mb-6 bg-slate-200 animate-pulse rounded" />
            </div>
            <div className="w-full md:w-7/12">
              <Card className="shadow-lg">
                <CardContent className="p-8">
                  <div className="h-8 w-3/4 bg-slate-200 animate-pulse rounded mb-4" />
                  <div className="h-4 w-full bg-slate-200 animate-pulse rounded" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Flow not found</h1>
          <p className="text-muted-foreground">The payment flow you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Special layout for referral flows
  if (flow?.flow_type === 'referral') {
    const referralTyDesign: any = (flow as any)?.thank_you_design || undefined;
    const referralStyles = resolveThankYouStyles(referralTyDesign);
    const referralHeading = referralTyDesign?.headingText ?? "You're in!";
    const referralButtonText = referralTyDesign?.buttonText ?? "Continue";
    const referralMessage = flow.thank_you_message || getDefaultThankYouMessage(flow.flow_type);
    const referralShareLabel = referralTyDesign?.shareLabel?.trim() || "Share your link";
    const referralReward = (flow as any).referral_reward as string | undefined;
    // Accent falls through: thank-you icon accent (custom) → the referral
    // design's accent color picked in the wizard → purple fallback.
    const accent = referralTyDesign?.iconAccent || getFlowAccent(flow, "#7c3aed");
    // The Continue button inherits the same accent unless the owner overrode
    // either button color in the wizard. resolveThankYouStyles applies its
    // own #111827 default for buttonBgColor when unset, so we explicitly
    // override here to keep referral flows brand-coherent.
    const referralButtonStyle = {
      ...referralStyles.button,
      backgroundColor: referralTyDesign?.buttonBgColor || accent,
      color: referralTyDesign?.buttonTextColor || contrastTextFor(accent),
    } as React.CSSProperties;
    const customShareUrl = referralTyDesign?.shareUrl?.trim();
    const normalizedCustomShareUrl = customShareUrl
      ? (/^https?:\/\//i.test(customShareUrl) ? customShareUrl : `https://${customShareUrl}`)
      : null;
    const shareUrl = normalizedCustomShareUrl || `${window.location.origin}/flow/${flowId}`;
    const shareText = `Check out ${flow.product_name}`;
    const copyShareLink = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Referral link copied to clipboard");
      } catch {
        toast.error("Couldn't copy — copy the link manually");
      }
    };
    // Use programmatic window.open so we can detect popup blockers (common when
    // the flow is embedded in an iframe without `allow="popups"`) and fall back
    // to copying the share URL. Also cancel the auto-redirect so the user isn't
    // navigated away mid-share.
    const openShare = (url: string, networkLabel: string) => {
      cancelRedirect();
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win || win.closed || typeof win.closed === "undefined") {
        navigator.clipboard?.writeText(shareUrl).then(
          () => toast.info(`${networkLabel} popup blocked — link copied instead`),
          () => toast.error(`${networkLabel} popup blocked — copy the link manually`)
        );
      }
    };
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <Card className="shadow-xl border-0 overflow-hidden bg-white/90 backdrop-blur-sm">
            <CardContent className="p-7 sm:p-8">
              {/* Header: brand badge sits with the success state instead of floating below it */}
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  {flow.logo_url ? (
                    <img
                      src={flow.logo_url}
                      alt={flow.business_name}
                      className="w-14 h-14 rounded-2xl object-cover bg-white shadow-sm ring-1 ring-gray-100"
                    />
                  ) : (
                    <div
                      className="w-14 h-14 rounded-2xl text-white flex items-center justify-center text-xl font-semibold shadow-sm"
                      style={{ backgroundColor: accent }}
                    >
                      {flow.business_name ? flow.business_name.charAt(0) : flow.product_name?.charAt(0)}
                    </div>
                  )}
                  <div
                    className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white"
                    style={{ backgroundColor: accent }}
                  >
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </div>
                </div>
                <h1 className="leading-tight mb-2" style={referralStyles.heading}>
                  {referralHeading}
                </h1>
                <p className="max-w-sm" style={referralStyles.message}>
                  {referralMessage}
                </p>
              </div>

              {/* Reward — visually weighty since it's the user's payoff */}
              {referralReward && (
                <div
                  className="mt-7 p-4 rounded-2xl border"
                  style={{ backgroundColor: `${accent}10`, borderColor: `${accent}33` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
                      <Gift className="w-5 h-5" style={{ color: accent }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: accent }}>Your reward</p>
                      <p className="text-base font-semibold text-gray-900 leading-snug break-words">{referralReward}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Share — input + copy + socials grouped as one block, tied together by a single bordered surface */}
              <div className="mt-7">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{referralShareLabel}</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                  <div className="flex items-stretch">
                    <input
                      readOnly
                      value={shareUrl}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 min-w-0 px-3.5 py-3 text-sm text-gray-700 bg-transparent focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={copyShareLink}
                      className="px-4 text-sm font-medium hover:opacity-80 border-l border-gray-200 transition-colors flex items-center gap-1.5"
                      style={{ color: accent }}
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </button>
                  </div>
                  <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2.5 flex items-center justify-center gap-1.5">
                    <span className="text-[11px] text-gray-500 mr-1">Or share via</span>
                    <button
                      type="button"
                      onClick={() => openShare(twitterUrl, "Twitter")}
                      aria-label="Share on Twitter"
                      className="w-8 h-8 rounded-full hover:bg-white text-gray-500 hover:shadow-sm flex items-center justify-center transition-all hover:[color:var(--accent)]"
                      style={{ ['--accent' as any]: accent }}
                    >
                      <Twitter className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openShare(facebookUrl, "Facebook")}
                      aria-label="Share on Facebook"
                      className="w-8 h-8 rounded-full hover:bg-white text-gray-500 hover:shadow-sm flex items-center justify-center transition-all hover:[color:var(--accent)]"
                      style={{ ['--accent' as any]: accent }}
                    >
                      <Facebook className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openShare(linkedinUrl, "LinkedIn")}
                      aria-label="Share on LinkedIn"
                      className="w-8 h-8 rounded-full hover:bg-white text-gray-500 hover:shadow-sm flex items-center justify-center transition-all hover:[color:var(--accent)]"
                      style={{ ['--accent' as any]: accent }}
                    >
                      <Linkedin className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Redirect: honors the owner's button styling from the wizard */}
              {flow?.redirect_url && (
                <div className="mt-7 pt-5 border-t border-gray-100">
                  <Progress value={progress} className="h-1 mb-3" />
                  <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                    <span>Redirecting in {timeLeft} second{timeLeft === 1 ? "" : "s"}</span>
                    <button
                      type="button"
                      onClick={handleRedirect}
                      className="px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
                      style={referralButtonStyle}
                    >
                      {referralButtonText}
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {!isPaid && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Powered by <a href="https://willtheyconvert.com/" className="text-gray-600 hover:text-gray-900 underline">willtheyconvert.com</a>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original layout for non-referral flows
  const tyDesign: any = (flow as any)?.thank_you_design || undefined;
  const tyStyles = resolveThankYouStyles(tyDesign);
  const headingText = tyDesign?.headingText ?? "Thank you!";
  const buttonText = tyDesign?.buttonText ?? "Continue now";
  // Accent falls through: thank-you icon accent (custom) → the flow's brand
  // accent picked in the wizard (per type) → emerald fallback. Keeps the
  // thank-you screen feeling like the same brand as the rest of the flow.
  const iconAccent = tyDesign?.iconAccent || getFlowAccent(flow);

  const accent = iconAccent || "#10b981";
  // Continue button inherits the same accent unless the owner explicitly
  // customized either button color in the wizard. resolveThankYouStyles
  // applies its own #111827 default for buttonBgColor when unset, so we
  // override here to keep all flow types brand-coherent.
  const continueButtonStyle = {
    ...tyStyles.button,
    backgroundColor: tyDesign?.buttonBgColor || accent,
    color: tyDesign?.buttonTextColor || contrastTextFor(accent),
  } as React.CSSProperties;
  const customNote = (flow as any)?.thank_you_note;
  const defaultNote = getDefaultThankYouNote(flow?.flow_type);
  const noteToShow = customNote || defaultNote;
  const redirectUrl = flow?.redirect_url
    ? (flow.redirect_url.startsWith('http') ? flow.redirect_url : `https://${flow.redirect_url}`)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <Card className="shadow-xl border-0 overflow-hidden bg-white/90 backdrop-blur-sm rounded-3xl">
          <CardContent className="p-7 sm:p-8">
            {/* Header — centered check + heading + message, mirrors the referral layout */}
            <div className="flex flex-col items-center text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm mb-5"
                style={{ backgroundColor: `${accent}1a` }}
              >
                <Check className="h-7 w-7" strokeWidth={3} style={{ color: accent }} />
              </div>
              <h1 className="leading-tight mb-2" style={tyStyles.heading}>{headingText}</h1>
              <p className="max-w-sm" style={tyStyles.message}>
                {flow?.thank_you_message || getDefaultThankYouMessage(flow?.flow_type)}
              </p>
              {selectedTier && (
                <p className="text-sm mt-2" style={tyStyles.message}>
                  You have selected the {selectedTier.name} plan at ${selectedTier.price}/{selectedTier.pricePeriod}.
                </p>
              )}
            </div>

            {/* Note as a soft callout instead of bare paragraph */}
            {noteToShow && (
              <div className="mt-7 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-100">
                <p className="text-xs leading-relaxed" style={tyStyles.note}>
                  <span style={{ ...tyStyles.note, fontWeight: 600 }}>Note · </span>
                  {noteToShow}
                </p>
              </div>
            )}

            {/* Redirect — soft surface for the destination, slim countdown, brand-styled CTA */}
            {redirectUrl && (
              <div className="mt-7 pt-5 border-t border-gray-100 space-y-4">
                <div className="px-4 py-3 rounded-2xl bg-gray-50 border border-gray-100">
                  <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">You'll be redirected to</p>
                  <p className="text-sm font-medium text-gray-900 break-all">{redirectUrl}</p>
                </div>
                <div>
                  <Progress value={progress} className="h-1 mb-2" />
                  <p className="text-xs text-gray-500">Redirecting in {timeLeft} second{timeLeft === 1 ? "" : "s"}</p>
                </div>
                <Button
                  className="w-full h-11 hover:opacity-90 rounded-xl"
                  style={continueButtonStyle}
                  onClick={handleRedirect}
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {!isPaid && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Powered by <a href="https://willtheyconvert.com/" className="text-gray-600 hover:text-gray-900 underline">willtheyconvert.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
